"use client"

import { Loader2 } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useRemoveStorefrontWidgetMutation } from "@/hooks/mutations/shopify-storefront-widget"
import type { StorefrontWidgetStoreView } from "@/server-actions/shopify/storefront-widget"

interface ShopifyRemoveChatDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  store: StorefrontWidgetStoreView
}

/** Confirms taking the chat off a live storefront. */
export function ShopifyRemoveChatDialog({ open, onOpenChange, store }: ShopifyRemoveChatDialogProps) {
  const remove = useRemoveStorefrontWidgetMutation()

  return (
    <AlertDialog open={open} onOpenChange={next => { if (!remove.isPending) onOpenChange(next) }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="break-words">{`Remove the chat from ${store.publicDomain}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            The chat disappears from your storefront; pages Shopify has cached can show it for a few more minutes. You
            can add it again at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            className="bg-red-600 hover:bg-red-700"
            onClick={event => {
              // Radix closes on click; stay open until the store answers.
              event.preventDefault()
              remove.mutate({ integrationId: store.integrationId }, { onSuccess: () => onOpenChange(false) })
            }}
          >
            {remove.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>Remove chat</span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
